/**
 * Image Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 854-2064
 *
 * An image container with optional close button and icon overlay.
 * Supports empty (placeholder) and filled (with source) states.
 */

import React from 'react';
import {
  View,
  Image as RNImage,
  StyleSheet,
  Pressable,
  ImageSourcePropType,
  ImageStyle,
  StyleProp,
} from 'react-native';
import { colors } from '../../tokens';
import { XIcon, ImageIcon } from '../../icons';

export type ImageState = 'default' | 'filled';

export interface ImageComponentProps {
  /**
   * Image source - when provided, shows "filled" state
   */
  source?: ImageSourcePropType;

  /**
   * Image size (width and height)
   * @default 88
   */
  size?: number;

  /**
   * Border radius
   * @default 4
   */
  borderRadius?: number;

  /**
   * Show close (x) button
   * @default true
   */
  showClose?: boolean;

  /**
   * Show placeholder icon when no image
   * @default true
   */
  showIcon?: boolean;

  /**
   * Callback when close button is pressed
   */
  onClose?: () => void;

  /**
   * Callback when image is pressed
   */
  onPress?: () => void;

  /**
   * Custom image style
   */
  imageStyle?: StyleProp<ImageStyle>;

  /**
   * Test ID for testing
   */
  testID?: string;
}

export const ImageComponent: React.FC<ImageComponentProps> = ({
  source,
  size = 88,
  borderRadius = 4,
  showClose = true,
  showIcon = true,
  onClose,
  onPress,
  imageStyle,
  testID,
}) => {
  const isFilled = !!source;

  const containerStyle = {
    width: size,
    height: size,
    borderRadius,
  };

  const handleClose = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    onClose?.();
  };

  const content = (
    <View
      style={[styles.container, containerStyle]}
      testID={testID}
      accessibilityRole="image"
    >
      {isFilled ? (
        <>
          <RNImage
            source={source}
            style={[styles.image, { borderRadius }, imageStyle]}
            resizeMode="cover"
          />
          <View style={[styles.overlay, { borderRadius }]} />
        </>
      ) : (
        <View style={[styles.placeholder, { borderRadius }]}>
          {showIcon && (
            <ImageIcon
              size={size * 0.27} // ~24px for 88px container
              color={colors.slate[400]}
            />
          )}
        </View>
      )}

      {showClose && (
        <Pressable
          style={styles.closeButton}
          onPress={handleClose}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Remove image"
        >
          <XIcon size={8} color={colors.slate[800]} />
        </Pressable>
      )}
    </View>
  );

  if (onPress) {
    return (
      <Pressable onPress={onPress} accessibilityRole="button">
        {content}
      </Pressable>
    );
  }

  return content;
};

ImageComponent.displayName = 'ImageComponent';

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(2, 2, 2, 0.2)',
  },
  placeholder: {
    flex: 1,
    backgroundColor: colors.neutral[200],
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 16,
    height: 16,
    borderRadius: 99,
    backgroundColor: colors.slate[200],
    alignItems: 'center',
    justifyContent: 'center',
  },
});
